import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, Events, scrollSpy, animateScroll as scroll } from 'react-scroll';

/**
 * CategorySubNav component for menu page navigation
 * - Sticks to top of viewport on scroll
 * - Horizontally scrollable on mobile
 * - Highlights current active category
 * 
 * @param {Object} props Component props
 * @param {Array} props.categories Array of menu categories
 * @returns {JSX.Element} CategorySubNav component
 */
const CategorySubNav = ({ categories }) => {
  const [activeCategory, setActiveCategory] = useState('');
  const [isFixed, setIsFixed] = useState(false);
  const navRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Use Intersection Observer to detect when nav should be fixed
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const nav = navRef.current;
    
    if (!sentinel || !nav) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is not intersecting (scrolled past), make nav fixed
        setIsFixed(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px'
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Initialize scrollspy
  useEffect(() => {
    // Register scrollspy events
    Events.scrollEvent.register('begin', () => {});
    Events.scrollEvent.register('end', () => {});
    
    // Register a callback for when a section becomes active
    const handleSetActive = (to) => {
      setActiveCategory(to);
    };
    
    Events.scrollEvent.register('setActive', handleSetActive);
    
    // Initialize scrollspy
    scrollSpy.update();
    
    return () => {
      // Clean up scrollspy events
      Events.scrollEvent.remove('begin');
      Events.scrollEvent.remove('end');
      Events.scrollEvent.remove('setActive');
    };
  }, []);

  // Scroll active item into view when it changes
  useEffect(() => {
    if (activeCategory && scrollContainerRef.current) {
      const activeElement = document.getElementById(`nav-item-${activeCategory}`);
      if (activeElement) {
        // Scroll the active element into view
        const container = scrollContainerRef.current;
        const scrollLeft = activeElement.offsetLeft - (container.clientWidth / 2) + (activeElement.clientWidth / 2);
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [activeCategory]);

  // Filter out categories with no items
  const validCategories = categories.filter(category => category._id);

  if (validCategories.length === 0) {
    return null;
  }

  // Calculate offset for scrolling - account for sticky nav height
  const getOffset = () => {
    // Account for CategorySubNav height when sticky (approx 70px)
    // Plus additional padding to ensure content is fully visible
    const subNavHeight = 70;
    const additionalPadding = 20;
    
    return -(subNavHeight + additionalPadding);
  };

  // State to track scroll shadow indicators
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(true);

  // Handle scroll event to update shadow indicators
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      
      // Show left shadow when scrolled right
      setShowLeftShadow(scrollLeft > 10);
      
      // Show right shadow when not at the end
      setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      
      // Initial check
      handleScroll();
      
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    scroll.scrollToTop({
      duration: 800,
      smooth: 'easeInOutQuart'
    });
  };

  return (
    <>
      {/* Sentinel element - invisible marker at the position where nav should become fixed */}
      <div ref={sentinelRef} className="h-0 w-full" />
      
      {/* Placeholder to prevent layout shift when nav becomes fixed */}
      {isFixed && <div className="h-[70px] w-full bg-white" />}
      
      <div 
        ref={navRef} 
        className={`bg-brand-navy w-full z-40 shadow-md transition-all duration-200 ${
          isFixed ? 'fixed top-0 left-0 right-0' : 'relative'
        }`}
      >
        <div className="relative">
        {/* Left scroll indicator */}
        <div 
          className={`absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-brand-navy to-transparent z-10 pointer-events-none transition-opacity duration-300 ${
            showLeftShadow ? 'opacity-100' : 'opacity-0'
          }`}
        ></div>
        
        {/* Scrollable container */}
        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto scrollbar-hide py-4 px-4 md:px-8"
          onScroll={handleScroll}
        >
          <div className="flex min-w-max px-2">
            {validCategories.map((category, index) => (
              <React.Fragment key={category._id}>
                {/* Add vertical separator between items (except before the first item) */}
                {index > 0 && (
                  <div className="mx-6 md:mx-8 self-center h-5 w-px bg-blue-200 opacity-30"></div>
                )}
                
                <div className="flex items-center group">
                  <Link
                    id={`nav-item-${category._id}`}
                    to={category._id}
                    spy={true}
                    smooth={true}
                    offset={getOffset()}
                    duration={500}
                    className={`flex items-center text-base md:text-lg font-medium cursor-pointer transition-all duration-200 whitespace-nowrap px-2 py-2 focus:outline-none focus:ring-2 focus:ring-brand-orange/50 rounded-md ${activeCategory === category._id ? '!text-brand-orange font-bold' : 'text-white hover:text-brand-orange focus:text-brand-orange'}`}
                    activeClass="!text-brand-orange font-bold"
                    onSetActive={() => setActiveCategory(category._id)}
                  >
                    {/* Thumbnail image in front of text */}
                    <div className={`mr-3 w-8 h-8 rounded-full overflow-hidden border-2 transition-all duration-200 flex-shrink-0 ${
                      activeCategory === category._id 
                        ? 'border-brand-orange' 
                        : 'border-transparent group-hover:border-brand-orange/50 group-focus:border-brand-orange/50'
                    }`}>
                      {category.imageUrl ? (
                        <img 
                          src={category.imageUrl} 
                          alt={category.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-brand-navy flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {category.name}
                  </Link>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Right scroll indicator */}
        <div 
          className={`absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-brand-navy to-transparent z-10 pointer-events-none transition-opacity duration-300 ${
            showRightShadow ? 'opacity-100' : 'opacity-0'
          }`}
        ></div>
      </div>
    </div>

    {/* Back to Top Button - appears when nav is fixed */}
    <button
      onClick={scrollToTop}
      className={`fixed bottom-6 right-6 z-50 bg-brand-navy text-white p-4 rounded-full shadow-lg hover:bg-brand-orange transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 ${
        isFixed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16 pointer-events-none'
      }`}
      aria-label="Back to top"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-6 w-6" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M5 10l7-7m0 0l7 7m-7-7v18" 
        />
      </svg>
    </button>
    </>
  );
};

CategorySubNav.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      slug: PropTypes.string,
    })
  ).isRequired,
};

export default CategorySubNav;
